import { BlobIdImageElementComposerViewModel } from "client/Api/Hub/common.composer";
import { IObjectInputAttachable, IValueInputAttachable } from "client/Forms/attachable";
import {
	ensureNotNil,
	generateTrackingId,
	getDataUrl,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { useBuilderEditor } from "../../useBuilderEditor";
import styles from "./Image.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { getHubImageUrl } from "client/Api/Hub/common";
import classNames from "classnames";
import {
	DropOverMenu,
	FormattedText,
	Icon,
	ValidationError
} from "client/Components";
import { ImageElementFormModel } from "client/Features/Hub/Management/common";
import { hubItemKindToHubDataSourceMap } from "client/Features/Hub/Sidebar/common";
import { Base64FileUriUploadWithMetadataAndAttributionModel, fileMetadataSymbol } from "client/Misc/commonTypes";
import { ElementLayout } from "../ElementLayout";
import { ElementGenericActionHandlers, ElementGenericHandle } from "../Element";
import { localize } from "client_legacy/ndaw/culture";
import { SelectImageMenu } from "../../Layout/Menus/SelectImageMenu";
import { HubImageSelectorKind, ImageWithAttribution } from "client/Features/Hub/Management/useHubImageSelector";
import { ImageAttributionSaveModel } from "client/Api/common";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { getDisplayFriendlySize } from "client_legacy/Documents/fileExtensions";
import { getClientId, hasClientId } from "client/Misc/clientId";
import { Guid } from "client_legacy/ndaw/guid";

export type ImageProps = {
	hubItemKind: HubItemKind;
	attachable: IValueInputAttachable<ImageElementFormModel>;
	originalImage: BlobIdImageElementComposerViewModel | null;
	genericHandlers: ElementGenericActionHandlers;
	isPixabayImageGalleryAvailable: boolean;
	maxImageSize: number;
	ref: Ref<ElementGenericHandle>;
};

export const Image = (props: ImageProps): ReactElement | null => {
	const {
		hubItemKind,
		attachable,
		originalImage,
		genericHandlers,
		isPixabayImageGalleryAvailable,
		maxImageSize,
		ref
	} = props;

	const {
		onAddNewElementToColumn,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onMoveUp,
		onMoveDown,
		onDuplicate,
		onDelete
	} = genericHandlers;

	const [isSelectMenuOpen, setIsSelectMenuOpen] = useState<boolean>(false);

	const rootRef: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const editButtonRef: RefObject<HTMLButtonElement | null> = useRef<HTMLButtonElement>(null);

	const {
		activate
	} = useBuilderEditor();

	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const imageAttachable: IObjectInputAttachable<OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel>> = attachable
		.for("image");

	const image: OptionalModel<Base64FileUriUploadWithMetadataAndAttributionModel> = imageAttachable.value;

	const src = useMemo(
		(): string | undefined => {
			if (image.isSet) {
				return image.value.uri;
			}

			if (isNotNil(originalImage)) {
				return getHubImageUrl(
					hubItemKindToHubDataSourceMap[hubItemKind],
					originalImage.blobId
				);
			}

			return undefined;
		},
		[
			hubItemKind,
			image,
			originalImage
		]
	);

	const imageName: string | undefined = image.isSet
		? image.value.filename
		: originalImage?.name;

	const hasError: boolean = !attachable.validation.isValid();

	const closeAllMenusHandler = useCallback(
		(): void => {
			setIsSelectMenuOpen(false);
		},
		[]
	);

	const editImageHandler = useCallback(
		async(file: File, attribution: ImageAttributionSaveModel | null): Promise<void> => {
			if (file.size > maxImageSize) {
				alertManager.showError(
					<FormattedText
						text={localize("_MaxSizeIs")}
						thingsToReplace={[
							{
								what: "{0}",
								filler: getDisplayFriendlySize(maxImageSize)
							}
						]}
					/>
				);
			}

			try {
				const dataUrl: string = await getDataUrl(file);

				const imageAttachable = (attachable as IValueInputAttachable<ImageElementFormModel>)
					.for("image");

				imageAttachable.reducer
					.setValue({
						isSet: true,
						value: {
							filename: file.name,
							uri: dataUrl,
							[fileMetadataSymbol]: {
								size: file.size,
								mimeType: file.type
							},
							attribution
						}
					});

				imageAttachable.validation
					.clearServerErrors();

				imageAttachable.validation
					.triggerInputFinished();
			} catch(e) {
				console.error(e);
			}
		},
		[
			attachable,
			alertManager,
			maxImageSize
		]
	);

	const selectImageHandler = useCallback(
		async (kind: HubImageSelectorKind): Promise<void> => {
			setIsSelectMenuOpen(false);

			const result: TryResult<ImageWithAttribution> = await onSelectImage(kind);

			if (!result.wasSuccessful) {
				return;
			}

			const {
				file,
				attribution
			} = result.result;

			editImageHandler(file, attribution);
		},
		[onSelectImage, editImageHandler]
	);

	useImperativeHandle(
		ref,
		(): ElementGenericHandle => ({
			findAndFocus: (clientId: Guid): void => {
				if (hasClientId(attachable.value) && getClientId(attachable.value) === clientId) {
					ensureNotNil(rootRef.current)
						.focus();
				}
			},
			scrollIntoView: (): void => {
				ensureNotNil(rootRef.current)
					.scrollIntoView();
			}
		}),
		[attachable.value]
	);

	return (
		<ElementLayout
			hasError={hasError}
			trackingId={attachable.reducer.path}
			onCloseAllMenus={closeAllMenusHandler}
			ref={rootRef}
		>
			<ElementLayout.ActionsMenu trackingId="element-actions">
				<ElementLayout.AddElementMenuItem
					onAddNewElementToColumn={onAddNewElementToColumn}
					onOpenEmbedInputDialog={onOpenEmbedInputDialog}
					onOpenVideoInputDialog={onOpenVideoInputDialog}
					onSelectImage={onSelectImage}
					trackingId={generateTrackingId(attachable.reducer.path, "__add-element-menu")}
				/>
				{onMoveUp && (
					<ElementLayout.MoveUpMenuItem
						onClick={onMoveUp}
					/>
				)}
				{onMoveDown && (
					<ElementLayout.MoveDownMenuItem
						onClick={onMoveDown}
					/>
				)}
				<ElementLayout.EditMenuItem
					ref={editButtonRef}
					onClick={() => setIsSelectMenuOpen(true)}
				/>
				{isSelectMenuOpen && (
					<DropOverMenu
						target={ensureNotNil(editButtonRef.current)}
						onClose={() => setIsSelectMenuOpen(false)}
						title={localize("_SelectImage")}
						trackingId="select-image-dropover"
					>
						<SelectImageMenu
							trackingId="select-image-menu"
							onSelectImage={selectImageHandler}
							title={isResponsive ? undefined : localize("_SelectImage")}
							isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
						/>
					</DropOverMenu>
				)}
				<ElementLayout.DuplicateMenuItem
					onClick={onDuplicate}
				/>
				<ElementLayout.DeleteMenuItem
					onClick={onDelete}
				/>
			</ElementLayout.ActionsMenu>
			<div
				{...setTrackingId(imageAttachable.reducer.path, "__image")}
				onClick={() => activate()}
				className={classNames({
					[styles.hasError]: hasError
				})}
			>
				{hasError
					? (
						<Icon
							value={{
								kind: "svg",
								name: "jpeg"
							}}
						/>
					)
					: (
						isNotNil(src) && (
							<img
								className={styles.image}
								alt={imageName}
								src={src}
							/>
						)
					)
				}
			</div>
			<ValidationError {...attachable.for("image").attachToValidationError()} />
		</ElementLayout>
	);
};
Image.displayName="Image";